import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  Tabs,
  DataTable,
  Badge,
  Button,
  Modal,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Select,
  Toast,
  Link,
  ButtonGroup,
  Tooltip,
  Box,
  Pagination,
  Spinner
} from '@shopify/polaris';
import { getRequest, postRequest, formatDate, formatToCurrency, truncate } from '../../shared/components/functions.jsx';
import PaymentIntentBadge from '../../shared/components/banking/PaymentIntentBadge';
import { intentApi, formatIban, signingTarget } from '../../shared/components/banking/paymentIntents';
import { TabTitle } from '../../shell/helmet';

const MATCH_STATUS_BADGES = {
  unmatched: <Badge tone="attention">Unmatched</Badge>,
  suggested: <Badge tone="warning">Suggestion</Badge>,
  matched: <Badge tone="success">Matched</Badge>,
  partial: <Badge tone="warning">Partially matched</Badge>,
  ignored: <Badge>Ignored</Badge>
};

// what a transaction can be booked on — the modal defaults to the invoices on the side of the money,
// credit notes are an explicit choice (never auto-matched)
const MATCH_TARGETS = {
  invoice_out: { option: 'Customer invoices', heading: 'an outgoing customer invoice', search: 'Search by invoice number or customer name', plural: 'customer invoices', path: '/invoices' },
  invoice_in: { option: 'Supplier invoices', heading: 'an incoming supplier invoice', search: 'Search by reference, number or supplier name', plural: 'supplier invoices', path: '/invoices_in' },
  creditnote_out: {
    option: 'Customer credit notes',
    heading: 'a customer credit note',
    search: 'Search by credit note number, credited invoice or customer name',
    plural: 'customer credit notes',
    path: '/creditnotes'
  },
  creditnote_in: {
    option: 'Supplier credit notes',
    heading: 'a supplier credit note',
    search: 'Search by reference, number or supplier name',
    plural: 'supplier credit notes',
    path: '/creditnotes_in'
  }
};
const MATCH_TARGET_OPTIONS = Object.entries(MATCH_TARGETS).map(([value, target]) => ({ label: target.option, value }));
const documentUrl = (type, id) => `${(MATCH_TARGETS[type] || MATCH_TARGETS.invoice_out).path}/${id}`;
const isCreditnote = (type) => String(type).startsWith('creditnote');
// deduct = a credit note netted against invoices in the same transaction: it counts negative
const signedAmount = (item) => (item.deduct ? -1 : 1) * (parseFloat(item.amount) || 0);

const formatDateTimeShort = (value) => {
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// compare on the DISPLAYED (local) date: value dates come in as UTC instants that can
// sit on the previous UTC day (Brussels midnight = 22:00Z), so a raw-string prefix
// comparison against UTC-today misses them
const isToday = (value) => value && formatDate(value) === formatDate(new Date());
const TODAY_ROW_STYLE = { backgroundColor: '#FFF2CC' };

const amountCell = (amount, currency) => (
  <Text as="span" fontWeight="semibold" tone={amount >= 0 ? 'success' : undefined}>
    {amount >= 0 ? '+' : ''}
    {formatToCurrency(amount)} {currency && currency !== 'EUR' ? currency : ''}
  </Text>
);

export default function BankingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [toast, setToast] = useState(false);
  const [toastError, setToastError] = useState(false);
  const [toastContent, setToastContent] = useState('Saved');
  const [syncing, setSyncing] = useState(false);

  // transactions tab
  const [transactions, setTransactions] = useState([]);
  const [counts, setCounts] = useState({});
  const [txFilter, setTxFilter] = useState('open'); // default: everything the back office still has to process
  const [txSearch, setTxSearch] = useState('');
  const [txSearchDebounced, setTxSearchDebounced] = useState('');
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);
  const [txSums, setTxSums] = useState({ sumAmount: 0, sumIn: 0, sumOut: 0 }); // over the whole filtered set, not just the page
  const [txLoading, setTxLoading] = useState(false);
  const [txAccountId, setTxAccountId] = useState('');
  const [txDirection, setTxDirection] = useState('');
  const [txDateFrom, setTxDateFrom] = useState('');
  const [txDateTo, setTxDateTo] = useState('');
  const [txAmountMin, setTxAmountMin] = useState('');
  const [txAmountMax, setTxAmountMax] = useState('');
  const [reviewTx, setReviewTx] = useState(null); // transaction in the match modal
  const [candType, setCandType] = useState(''); // '' = let the server pick by direction
  const [candResolvedType, setCandResolvedType] = useState('invoice_out');
  const [candQuery, setCandQuery] = useState('');
  const [candQueryDebounced, setCandQueryDebounced] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [candInfo, setCandInfo] = useState(null);
  const [candLoading, setCandLoading] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [splitItems, setSplitItems] = useState([]); // one transaction split across several invoices: {type, invoiceId, number, party, open, amount}

  const [accounts, setAccounts] = useState([]);

  // payments tab: supplier payments sent to the bank from the invoice pages (payment intents)
  const [payments, setPayments] = useState([]);
  const [paymentBusyId, setPaymentBusyId] = useState(null);

  const showToast = (content, isError = false) => {
    setToastContent(content);
    isError ? setToastError(true) : setToast(true);
  };

  const fetchStatus = useCallback(async () => {
    const data = await getRequest('/api/ponto/status');
    if (data) setStatus(data);
  }, []);

  const TX_PAGE_SIZE = 50;

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true);
    const params = new URLSearchParams({ limit: TX_PAGE_SIZE, page: txPage });
    if (txFilter !== 'all') params.set('status', txFilter);
    if (txSearchDebounced) params.set('q', txSearchDebounced);
    if (txAccountId) params.set('accountId', txAccountId);
    if (txDirection) params.set('direction', txDirection);
    if (txDateFrom) params.set('dateFrom', txDateFrom);
    if (txDateTo) params.set('dateTo', txDateTo);
    if (txAmountMin) params.set('amountMin', txAmountMin);
    if (txAmountMax) params.set('amountMax', txAmountMax);
    const data = await getRequest(`/api/ponto/transactions?${params.toString()}`);
    if (data && Array.isArray(data.rows)) {
      setTransactions(data.rows);
      setTxTotal(data.total || 0);
      setTxSums({ sumAmount: data.sumAmount || 0, sumIn: data.sumIn || 0, sumOut: data.sumOut || 0 });
    }
    setTxLoading(false);
    const countData = await getRequest('/api/ponto/transactions/counts');
    if (Array.isArray(countData)) {
      setCounts(Object.fromEntries(countData.map((c) => [c.MatchStatus, c.amount])));
    }
  }, [txFilter, txSearchDebounced, txPage, txAccountId, txDirection, txDateFrom, txDateTo, txAmountMin, txAmountMax]);

  const fetchAccounts = useCallback(async () => {
    const data = await getRequest('/api/ponto/accounts');
    if (Array.isArray(data)) setAccounts(data);
  }, []);

  const fetchPayments = useCallback(async () => {
    const data = await getRequest('/api/ponto/payments');
    if (Array.isArray(data)) setPayments(data);
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchAccounts();
  }, [fetchStatus, fetchAccounts]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // debounce the free-text search so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setTxSearchDebounced(txSearch), 300);
    return () => clearTimeout(timer);
  }, [txSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setCandQueryDebounced(candQuery), 300);
    return () => clearTimeout(timer);
  }, [candQuery]);

  // candidates for the match modal
  useEffect(() => {
    if (!reviewTx) return;
    let cancelled = false;
    (async () => {
      setCandLoading(true);
      const params = new URLSearchParams();
      if (candType) params.set('type', candType);
      if (candQueryDebounced) params.set('q', candQueryDebounced);
      const data = await getRequest(`/api/ponto/transactions/${reviewTx.BankTransactionID}/match-candidates?${params.toString()}`);
      if (!cancelled && data && Array.isArray(data.candidates)) {
        setCandidates(data.candidates);
        setCandInfo(data.info || null);
        setCandResolvedType(data.type);
      }
      if (!cancelled) setCandLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewTx, candType, candQueryDebounced]);

  const closeMatchModal = () => {
    setReviewTx(null);
    setCandType('');
    setCandQuery('');
    setCandQueryDebounced('');
    setCandidates([]);
    setCandInfo(null);
    setSplitItems([]);
  };

  // any filter change jumps back to the first page
  useEffect(() => {
    setTxPage(1);
  }, [txFilter, txSearchDebounced, txAccountId, txDirection, txDateFrom, txDateTo, txAmountMin, txAmountMax]);

  useEffect(() => {
    if (selectedTab === 1) fetchPayments();
  }, [selectedTab, fetchPayments]);

  // feedback after the Ponto onboarding redirect
  useEffect(() => {
    const pontoResult = searchParams.get('ponto');
    if (!pontoResult) return;
    if (pontoResult === 'connected') showToast('Ponto connected successfully');
    if (pontoResult === 'error') showToast(`Ponto connection failed: ${searchParams.get('message') || 'unknown error'}`, true);
    searchParams.delete('ponto');
    searchParams.delete('message');
    setSearchParams(searchParams, { replace: true });
    fetchStatus();
    //eslint-disable-next-line
  }, []);

  const handleConnect = async () => {
    const data = await getRequest('/api/ponto/connect');
    if (data?.url) {
      window.location.href = data.url;
    } else {
      showToast('Could not build the Ponto authorization link. Is Ponto configured on the server?', true);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    const response = await fetch('/api/ponto/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
    const summary = await response.json().catch(() => null);
    setSyncing(false);
    if (response.ok && summary && !summary.skipped) {
      showToast(`Sync done: ${summary.newTransactions} new transactions, ${summary.matching?.matched || 0} auto-matched, ${summary.matching?.suggested || 0} suggestions`);
      fetchStatus();
      fetchTransactions();
      fetchAccounts();
    } else {
      showToast(summary?.reason || summary?.msg || 'Sync failed', true);
    }
  };

  const txAction = async (id, action, body = {}) => {
    setMatchLoading(true);
    const response = await fetch(`/api/ponto/transactions/${id}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    setMatchLoading(false);
    if (response.ok) {
      showToast('Transaction updated');
      closeMatchModal();
      fetchTransactions();
      return true;
    }
    const err = await response.json().catch(() => ({}));
    showToast(err.msg || 'Action failed', true);
    return false;
  };

  // withdraw a payment nobody signed yet (deleted in Ponto, the invoice simply stays open)
  const cancelPayment = async (payment) => {
    if (!window.confirm(`Cancel the payment of ${formatToCurrency(payment.Amount)} to ${payment.CreditorName || payment.CreditorIBAN}?`)) return;
    setPaymentBusyId(payment.PontoPaymentID);
    const result = await intentApi(`/${payment.PontoPaymentID}/cancel`, { method: 'POST' });
    setPaymentBusyId(null);
    if (result.ok) {
      showToast('Payment cancelled');
      fetchPayments();
    } else {
      showToast(result.data?.msg || 'Could not cancel the payment', true);
    }
  };

  //---------------------------------------------------------------------------
  // header / status
  //---------------------------------------------------------------------------
  const statusBanner = !status ? null : !status.configured ? (
    <Banner title="Ponto is not configured" tone="warning">
      <Text>
        Set <b>PONTO_CLIENT_ID</b> and <b>PONTO_CLIENT_SECRET</b> in the server environment (Ponto dashboard → Integrations → add a custom integration).
      </Text>
    </Banner>
  ) : !status.linked && status.integrationType === 'connect' ? (
    <Banner title="Ponto is configured but not linked to your organisation yet" tone="warning" action={{ content: 'Connect Ponto', onAction: handleConnect }}>
      <Text>
        Authorize this application in Ponto to give it access to your bank accounts ({status.environment} environment). Allowed redirect URI to whitelist: <b>{status.onboardingRedirectUri}</b>
      </Text>
    </Banner>
  ) : status.encryptionEnabled === false ? (
    <Banner title="Bank data is NOT encrypted" tone="warning">
      <Text>
        <b>BANK_DATA_ENCRYPTION_KEY</b> is not set: transactions are stored readable for anyone with database access. Generate a key and run the encryption migration before syncing sensitive data (see
        server/PONTO.md).
      </Text>
    </Banner>
  ) : status.matchingEnabled === false ? (
    <Banner title="Payment matching is disabled" tone="info">
      <Text>
        Transactions sync normally, but nothing is matched or booked on invoices (automatic or manual). Set <b>PONTO_MATCHING_ENABLED=true</b> in the server environment to enable matching.
      </Text>
    </Banner>
  ) : null;

  //---------------------------------------------------------------------------
  // transactions tab
  //---------------------------------------------------------------------------
  const openCount = (counts.unmatched || 0) + (counts.suggested || 0) + (counts.partial || 0);
  const txFilterTabs = [
    { id: 'open', content: `To process (${openCount})` },
    { id: 'all', content: `All` },
    { id: 'unmatched', content: `Unmatched (${counts.unmatched || 0})` },
    { id: 'suggested', content: `Suggestions (${counts.suggested || 0})` },
    { id: 'partial', content: `Partial (${counts.partial || 0})` },
    { id: 'matched', content: `Matched (${counts.matched || 0})` },
    { id: 'ignored', content: `Ignored (${counts.ignored || 0})` }
  ];

  const matchedInvoiceCell = (tx) => {
    // a transaction split across several documents, only partly allocated, or booked on a credit note: list every slice
    const partial = tx.MatchStatus === 'partial';
    if (Array.isArray(tx.allocations) && (tx.allocations.length > 1 || partial || tx.allocations.some((al) => isCreditnote(al.type)))) {
      return (
        <BlockStack gap="050">
          {tx.allocations.map((al, index) => (
            <Link key={`${al.type}-${al.invoiceId}-${index}`} removeUnderline url={documentUrl(al.type, al.invoiceId)}>
              {isCreditnote(al.type) ? 'CN ' : ''}
              {al.number} · {al.deduct ? '−' : ''}
              {formatToCurrency(al.amount)}
            </Link>
          ))}
          {partial ? (
            <Text as="span" tone="caution" variant="bodySm">
              {formatToCurrency(Math.abs(tx.Amount) - (tx.BookedAmount || 0))} still to allocate
            </Text>
          ) : null}
        </BlockStack>
      );
    }
    if (tx.MatchType === 'invoice_out' && tx.MatchedInvoiceID) {
      return (
        <Link removeUnderline url={`/invoices/${tx.MatchedInvoiceID}`}>
          {tx.matchedInvoiceNumber} {tx.matchedInvoiceCustomer ? `· ${truncate(tx.matchedInvoiceCustomer, 20)}` : ''}
        </Link>
      );
    }
    if (tx.MatchType === 'invoice_in' && tx.MatchedInvoiceID) {
      return (
        <BlockStack gap="050">
          <Link removeUnderline url={`/invoices_in/${tx.MatchedInvoiceID}`}>
            {tx.matchedInvoiceInNumber} {tx.matchedInvoiceSupplier ? `· ${truncate(tx.matchedInvoiceSupplier, 20)}` : ''}
          </Link>
          {tx.matchedInvoiceInReference ? (
            <Text as="span" tone="subdued" variant="bodySm">
              {truncate(tx.matchedInvoiceInReference, 24)}
            </Text>
          ) : null}
        </BlockStack>
      );
    }
    return '';
  };

  const matchingEnabled = status?.matchingEnabled !== false;

  const txRows = transactions.map((tx) => [
    <div style={isToday(tx.ValueDate) ? TODAY_ROW_STYLE : undefined}>{formatDate(tx.ValueDate)}</div>,
    <Tooltip content={`${tx.accountReference || ''} ${tx.accountDescription || ''}`}>
      <Text as="span">{truncate(tx.accountDescription || tx.accountReference, 14)}</Text>
    </Tooltip>,
    <Text as="span" fontWeight="medium">
      {truncate(tx.CounterpartName, 26) || '—'}
    </Text>,
    <Text as="span" variant="bodySm">
      {tx.CounterpartReference || '—'}
    </Text>,
    <Tooltip content={tx.RemittanceInformation || tx.Description || ''}>
      <Text as="span" tone="subdued" variant="bodySm">
        {truncate(tx.RemittanceInformation || tx.Description, 40) || '—'}
      </Text>
    </Tooltip>,
    tx.ogmInvoiceNumber ? (
      <Text as="span" fontWeight="medium">
        {tx.ogmInvoiceNumber}
      </Text>
    ) : (
      ''
    ),
    amountCell(tx.Amount, tx.Currency),
    <BlockStack gap="050">
      {MATCH_STATUS_BADGES[tx.MatchStatus] || tx.MatchStatus}
      {tx.MatchReason ? (
        <Text as="span" tone="subdued" variant="bodySm">
          {truncate(tx.MatchReason, 40)}
        </Text>
      ) : null}
    </BlockStack>,
    matchedInvoiceCell(tx),
    <ButtonGroup>
      {tx.MatchStatus === 'matched' || tx.MatchStatus === 'partial' ? (
        <>
          {tx.MatchStatus === 'partial' && matchingEnabled ? (
            <Button size="slim" variant="primary" onClick={() => setReviewTx(tx)}>
              Match remainder
            </Button>
          ) : null}
          <Button size="slim" onClick={() => txAction(tx.BankTransactionID, 'unmatch')}>
            Unmatch
          </Button>
        </>
      ) : (
        <>
          {matchingEnabled ? (
            <Button size="slim" variant="primary" onClick={() => setReviewTx(tx)}>
              {tx.MatchStatus === 'suggested' ? 'Review' : 'Match'}
            </Button>
          ) : null}
          {tx.MatchStatus !== 'ignored' ? (
            <Button size="slim" onClick={() => txAction(tx.BankTransactionID, 'ignore')}>
              Ignore
            </Button>
          ) : (
            <Button size="slim" onClick={() => txAction(tx.BankTransactionID, 'unmatch')}>
              Restore
            </Button>
          )}
        </>
      )}
    </ButtonGroup>
  ]);

  const txFiltersActive = Boolean(txSearch || txAccountId || txDirection || txDateFrom || txDateTo || txAmountMin || txAmountMax);
  const txTotalPages = Math.max(1, Math.ceil(txTotal / TX_PAGE_SIZE));
  // totals row: net sum of every transaction in the current filter/tab (all pages)
  const txTotalsRow = ['', '', '', '', '', '', amountCell(txSums.sumAmount), '', '', ''];
  const txEmptyMessage =
    !txFiltersActive && txFilter === 'open'
      ? 'Nothing left to process: every transaction is matched or ignored.'
      : txFiltersActive || txFilter !== 'all'
      ? 'No transactions match the current filters.'
      : 'No transactions yet. Connect Ponto and run a sync.';

  const clearTxFilters = () => {
    setTxSearch('');
    setTxAccountId('');
    setTxDirection('');
    setTxDateFrom('');
    setTxDateTo('');
    setTxAmountMin('');
    setTxAmountMax('');
  };

  const transactionsTab = (
    <BlockStack gap="300">
      <Card padding="0">
        <Tabs tabs={txFilterTabs} selected={txFilterTabs.findIndex((t) => t.id === txFilter)} onSelect={(index) => setTxFilter(txFilterTabs[index].id)} />
        <Box padding="300" paddingBlockStart="0">
          <BlockStack gap="200">
            <TextField
              labelHidden
              label="Search"
              placeholder="Search counterparty, message or IBAN"
              value={txSearch}
              onChange={setTxSearch}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setTxSearch('')}
              suffix={txLoading ? <Spinner size="small" accessibilityLabel="Searching transactions" /> : null}
            />
            <InlineStack gap="200" blockAlign="end" wrap>
              <Select
                label="Account"
                options={[{ label: 'All accounts', value: '' }, ...accounts.map((a) => ({ label: a.Description || a.Reference, value: String(a.BankAccountID) }))]}
                value={txAccountId}
                onChange={setTxAccountId}
              />
              <Select
                label="Direction"
                options={[
                  { label: 'In & out', value: '' },
                  { label: 'Money in', value: 'in' },
                  { label: 'Money out', value: 'out' }
                ]}
                value={txDirection}
                onChange={setTxDirection}
              />
              <TextField label="From" type="date" value={txDateFrom} onChange={setTxDateFrom} autoComplete="off" />
              <TextField label="To" type="date" value={txDateTo} onChange={setTxDateTo} autoComplete="off" />
              <TextField label="Min €" type="number" value={txAmountMin} onChange={setTxAmountMin} autoComplete="off" />
              <TextField label="Max €" type="number" value={txAmountMax} onChange={setTxAmountMax} autoComplete="off" />
              {txFiltersActive ? <Button onClick={clearTxFilters}>Clear filters</Button> : null}
            </InlineStack>
          </BlockStack>
        </Box>
        <div style={{ opacity: txLoading ? 0.45 : 1, transition: 'opacity 150ms ease' }}>
          <DataTable
            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text', 'text']}
            headings={['Date', 'Account', 'Counterparty', 'IBAN', 'Message', 'OGM', 'Amount', 'Match status', 'Invoice', 'Actions']}
            rows={txRows}
            totals={transactions.length > 0 ? txTotalsRow : undefined}
            totalsName={{ singular: 'Total', plural: 'Total' }}
            showTotalsInFooter
            verticalAlign="middle"
            increasedTableDensity
          />
        </div>
        {transactions.length === 0 && txLoading && (
          <Box padding="400">
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" accessibilityLabel="Loading transactions" />
              <Text tone="subdued">Loading transactions…</Text>
            </InlineStack>
          </Box>
        )}
        {transactions.length === 0 && !txLoading && (
          <Box padding="400">
            <Text tone="subdued">{txEmptyMessage}</Text>
          </Box>
        )}
        {txTotal > 0 && (
          <Box padding="300">
            <InlineStack align="center" gap="300" blockAlign="center">
              <Pagination
                hasPrevious={txPage > 1}
                onPrevious={() => setTxPage((p) => Math.max(1, p - 1))}
                hasNext={txPage < txTotalPages}
                onNext={() => setTxPage((p) => p + 1)}
                label={`${txPage} / ${txTotalPages}`}
              />
              <Text tone="subdued" variant="bodySm">
                {txTotal} transaction{txTotal === 1 ? '' : 's'} · in {formatToCurrency(txSums.sumIn)} · out {formatToCurrency(txSums.sumOut)} · net {formatToCurrency(txSums.sumAmount)}
              </Text>
            </InlineStack>
          </Box>
        )}
      </Card>
    </BlockStack>
  );

  //---------------------------------------------------------------------------
  // payments tab
  //---------------------------------------------------------------------------
  const paymentRows = payments.map((p) => [
    <div style={isToday(p.CreatedAt) ? TODAY_ROW_STYLE : undefined}>{formatDate(p.CreatedAt)}</div>,
    p.FaktuurinID ? (
      <Link removeUnderline url={`/invoices_in/${p.FaktuurinID}`}>
        {p.faktuurref || p.FactuurInNummer}
      </Link>
    ) : (
      '—'
    ),
    truncate(p.CreditorName || p.invoiceSupplier, 30),
    formatToCurrency(p.Amount),
    <Text as="span" variant="bodySm">
      {formatIban(p.CreditorIBAN)}
    </Text>,
    <Tooltip content={`${p.remittanceDisplay || p.RemittanceInformation || ''} · account ${p.accountReference || ''}`}>
      <Text as="span" tone="subdued" variant="bodySm">
        {truncate(p.remittanceDisplay || p.RemittanceInformation, 24)}
      </Text>
    </Tooltip>,
    <BlockStack gap="050">
      <PaymentIntentBadge intent={p} />
      <Text as="span" tone="subdued" variant="bodySm">
        {p.Status}
      </Text>
    </BlockStack>,
    p.createdBy || '',
    <ButtonGroup>
      {p.state === 'awaiting_signature' ? (
        <Button size="slim" onClick={() => window.open(signingTarget(p).url, '_blank', 'noopener')}>
          {signingTarget(p).label}
        </Button>
      ) : null}
      {p.state === 'awaiting_signature' ? (
        <Button size="slim" tone="critical" loading={paymentBusyId === p.PontoPaymentID} onClick={() => cancelPayment(p)}>
          Cancel
        </Button>
      ) : null}
    </ButtonGroup>
  ]);

  const paymentsTab = (
    <Card padding="0">
      <Box padding="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text tone="subdued" variant="bodySm">
            Supplier payments created from the invoice pages. An invoice is only marked as paid once the bank's debit is matched.
          </Text>
          <Button
            size="slim"
            onClick={async () => {
              const data = await postRequest(
                '/api/ponto/payments/refresh',
                {},
                () => {},
                () => {}
              );
              showToast(`Refreshed ${data?.updated ?? 0} payment status(es)`);
              fetchPayments();
            }}
          >
            Refresh statuses
          </Button>
        </InlineStack>
      </Box>
      <DataTable
        columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text', 'text', 'text', 'text']}
        headings={['Sent', 'Invoice', 'Creditor', 'Amount', 'IBAN', 'Message', 'Status', 'By', 'Actions']}
        rows={paymentRows}
        verticalAlign="middle"
        increasedTableDensity
      />
      {payments.length === 0 && (
        <Box padding="400">
          <Text tone="subdued">No payments sent to the bank yet. Pay a supplier invoice from Invoices in → payment modal → "Send to bank".</Text>
        </Box>
      )}
    </Card>
  );

  //---------------------------------------------------------------------------
  // accounts tab
  //---------------------------------------------------------------------------
  const accountRows = accounts.map((a) => [
    a.Description || a.Product || '—',
    a.Reference,
    a.HolderName || '—',
    formatToCurrency(a.CurrentBalance),
    a.LastSyncedAt ? formatDateTimeShort(a.LastSyncedAt) : 'never',
    a.transactionCount,
    a.openTransactionCount,
    a.Active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>
  ]);

  const accountsTab = (
    <Card padding="0">
      <DataTable
        columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'numeric', 'numeric', 'text']}
        headings={['Account', 'IBAN', 'Holder', 'Balance', 'Last sync', 'Transactions', 'To review', 'Status']}
        rows={accountRows}
        verticalAlign="middle"
      />
      {accounts.length === 0 && (
        <Box padding="400">
          <Text tone="subdued">No bank accounts yet. Connect Ponto and run a sync.</Text>
        </Box>
      )}
    </Card>
  );

  //---------------------------------------------------------------------------
  // match / review modal — a transaction can be booked on one invoice (Match)
  // or split across several (Add builds a selection with per-invoice amounts)
  //---------------------------------------------------------------------------
  const txAbsAmount = reviewTx ? Math.abs(reviewTx.Amount) : 0;
  // a partially allocated transaction only has its remainder left to place
  const txIsPartial = reviewTx?.MatchStatus === 'partial';
  const txPlaceAmount = txIsPartial ? Math.round((txAbsAmount - (reviewTx.BookedAmount || 0)) * 100) / 100 : txAbsAmount;
  const splitTotal = Math.round(splitItems.reduce((sum, item) => sum + signedAmount(item), 0) * 100) / 100;
  const splitRemainder = Math.round((txPlaceAmount - splitTotal) * 100) / 100;
  // no invoice can take more than its open balance; the amount to place is either fully
  // allocated (Match) or the rest is explicitly kept open on the transaction (partial).
  // Deducted credit notes lower the total, so the invoices in the split must outweigh them.
  const splitFullyAllocated = Math.abs(splitRemainder) <= 0.01;
  const splitItemsValid = splitItems.length > 0 && splitTotal > 0.01 && splitItems.every((item) => parseFloat(item.amount) > 0 && parseFloat(item.amount) <= item.open + 0.01);
  const splitValid = splitItemsValid && splitFullyAllocated;
  const splitCanKeepRemainder = splitItemsValid && splitRemainder > 0.01;
  const splitNoun = splitItems.every((item) => !isCreditnote(item.type)) ? 'invoice(s)' : 'document(s)';
  const inSplit = (c) => splitItems.some((item) => item.type === c.type && item.invoiceId === c.invoiceId);
  const candDeduct = candidates.some((c) => c.deduct);

  const addToSplit = (c) => {
    if (inSplit(c)) return;
    // a deducted credit note is normally netted in full; anything else fills what is still unallocated
    const remaining = Math.max(0, splitRemainder);
    const defaultAmount = c.deduct ? c.open : Math.max(0, Math.min(c.open, remaining));
    setSplitItems((prev) => [...prev, { type: c.type, invoiceId: c.invoiceId, number: c.number, party: c.party, open: c.open, deduct: Boolean(c.deduct), amount: defaultAmount.toFixed(2) }]);
  };

  const submitSplit = (keepRemainder = false) =>
    txAction(reviewTx.BankTransactionID, 'match', {
      allocations: splitItems.map((item) => ({ type: item.type, invoiceId: item.invoiceId, amount: parseFloat(item.amount) })),
      ...(keepRemainder ? { keepRemainder: true } : {})
    });

  const splitPanel =
    splitItems.length === 0 ? null : (
      <Box background="bg-surface-secondary" borderRadius="200" padding="300">
        <BlockStack gap="200">
          <Text fontWeight="semibold">
            Split across {splitItems.length} {splitNoun}
          </Text>
          {splitItems.map((item, index) => (
            <InlineStack key={`${item.type}-${item.invoiceId}`} align="space-between" blockAlign="center" gap="200" wrap={false}>
              <BlockStack gap="0">
                <InlineStack gap="150" blockAlign="center">
                  <Text as="span" fontWeight="medium">
                    {isCreditnote(item.type) ? 'Credit note ' : ''}
                    {item.number}
                  </Text>
                  {item.deduct ? <Badge tone="attention">Deducted</Badge> : null}
                </InlineStack>
                <Text as="span" tone="subdued" variant="bodySm">
                  {truncate(item.party, 28)} · open {formatToCurrency(item.open)}
                </Text>
              </BlockStack>
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <Box maxWidth="130px">
                  <TextField
                    labelHidden
                    label={`Amount for ${isCreditnote(item.type) ? 'credit note' : 'invoice'} ${item.number}`}
                    type="number"
                    prefix={item.deduct ? '−€' : '€'}
                    value={item.amount}
                    onChange={(value) => setSplitItems((prev) => prev.map((it, i) => (i === index ? { ...it, amount: value } : it)))}
                    autoComplete="off"
                    error={parseFloat(item.amount) > item.open + 0.01 ? `max ${formatToCurrency(item.open)}` : undefined}
                  />
                </Box>
                <Button variant="plain" tone="critical" onClick={() => setSplitItems((prev) => prev.filter((_, i) => i !== index))}>
                  Remove
                </Button>
              </InlineStack>
            </InlineStack>
          ))}
          <InlineStack align="space-between" blockAlign="center">
            <Text tone={splitRemainder < -0.01 ? 'critical' : splitFullyAllocated ? 'success' : 'caution'}>
              {splitRemainder < -0.01
                ? `Over-allocated by ${formatToCurrency(-splitRemainder)}`
                : splitFullyAllocated
                ? 'Fully allocated'
                : `Unallocated: ${formatToCurrency(splitRemainder)} — allocate it, or keep it open on the transaction`}
            </Text>
            <ButtonGroup>
              {splitCanKeepRemainder ? (
                <Button loading={matchLoading} onClick={() => submitSplit(true)}>
                  Book {formatToCurrency(splitTotal)}, keep {formatToCurrency(splitRemainder)} open
                </Button>
              ) : null}
              <Button variant="primary" disabled={!splitValid} loading={matchLoading} onClick={() => submitSplit()}>
                Match {splitItems.length} {splitNoun} — {formatToCurrency(splitTotal)}
              </Button>
            </ButtonGroup>
          </InlineStack>
        </BlockStack>
      </Box>
    );

  const matchModal = (
    <Modal open={Boolean(reviewTx)} onClose={closeMatchModal} title="Match bank transaction" size="large">
      {reviewTx && (
        <>
          <Modal.Section>
            <BlockStack gap="200">
              <InlineStack gap="400" wrap>
                <Text fontWeight="semibold">{formatDate(reviewTx.ValueDate)}</Text>
                {amountCell(reviewTx.Amount, reviewTx.Currency)}
                <Text>{reviewTx.CounterpartName}</Text>
              </InlineStack>
              <Text tone="subdued">{reviewTx.RemittanceInformation || reviewTx.Description || 'No payment message'}</Text>
              {reviewTx.CounterpartReference ? <Text tone="subdued">Counterparty IBAN: {reviewTx.CounterpartReference}</Text> : null}
              {reviewTx.ogmInvoiceNumber ? <Text fontWeight="medium">OGM in message → invoice {reviewTx.ogmInvoiceNumber}</Text> : null}
              {txIsPartial ? (
                <Banner tone="warning">
                  <Text>
                    Already booked {formatToCurrency(reviewTx.BookedAmount || 0)}
                    {Array.isArray(reviewTx.allocations) && reviewTx.allocations.length > 0
                      ? ` on ${reviewTx.allocations.map((al) => `${al.number} (${al.deduct ? '−' : ''}${formatToCurrency(al.amount)})`).join(', ')}`
                      : ''}
                    {' · '}
                    <b>{formatToCurrency(txPlaceAmount)}</b> still to allocate
                  </Text>
                </Banner>
              ) : null}
            </BlockStack>
          </Modal.Section>
          <Modal.Section>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center" wrap>
                <Text fontWeight="semibold">
                  Money {reviewTx.Amount >= 0 ? 'in' : 'out'} — match with {(MATCH_TARGETS[candResolvedType] || MATCH_TARGETS.invoice_out).heading}
                </Text>
                <Select label="Book on" labelInline options={MATCH_TARGET_OPTIONS} value={candResolvedType} onChange={setCandType} />
              </InlineStack>
              {candInfo ? (
                <Banner tone="warning">
                  <Text>{candInfo}</Text>
                </Banner>
              ) : null}
              {candDeduct ? (
                <Banner tone="info">
                  <Text>
                    {reviewTx.Amount >= 0
                      ? 'A customer credit note on money in is deducted: the customer paid an invoice minus this credit note. Add the credit note and the invoice(s) to one split — both get settled.'
                      : 'A supplier credit note on money out is deducted: you paid a supplier invoice minus this credit note. Add the credit note and the invoice(s) to one split — both get settled.'}
                  </Text>
                </Banner>
              ) : null}
              {splitPanel}
              <TextField
                labelHidden
                label="Search documents"
                placeholder={(MATCH_TARGETS[candResolvedType] || MATCH_TARGETS.invoice_out).search}
                value={candQuery}
                onChange={setCandQuery}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setCandQuery('')}
              />
              {candLoading && candidates.length === 0 ? (
                <Text tone="subdued">Loading suggestions…</Text>
              ) : candidates.length === 0 ? (
                <Text tone="subdued">
                  No open {(MATCH_TARGETS[candResolvedType] || MATCH_TARGETS.invoice_out).plural} found{candQuery ? ' for this search' : ''}.
                </Text>
              ) : (
                <BlockStack gap="200">
                  {candidates.map((c) => (
                    <InlineStack key={`${c.type}-${c.invoiceId}`} align="space-between" blockAlign="center" wrap={false} gap="200">
                      <BlockStack gap="050">
                        <InlineStack gap="150" blockAlign="center" wrap>
                          <Link removeUnderline url={documentUrl(c.type, c.invoiceId)}>
                            <Text as="span" fontWeight="semibold">
                              {c.number}
                            </Text>
                          </Link>
                          <Text as="span">{truncate(c.party, 32)}</Text>
                          {c.amountExact ? <Badge tone="success">Exact amount</Badge> : null}
                          {c.reasons.map((reason) => (
                            <Badge key={reason} tone="info">
                              {reason}
                            </Badge>
                          ))}
                        </InlineStack>
                        <Text tone="subdued" variant="bodySm">
                          {c.credits ? `credits invoice ${c.credits} · ` : ''}
                          {c.reference ? `${c.reference} · ` : ''}
                          {formatDate(c.date)} · open {formatToCurrency(c.open)} of {formatToCurrency(c.total)}
                          {c.deduct
                            ? ' · deducted from the invoice(s) in the split'
                            : !c.amountExact && c.open > txPlaceAmount
                            ? ` · books ${formatToCurrency(txPlaceAmount)} as partial payment`
                            : !c.amountExact && c.open < txPlaceAmount - 0.01
                            ? ` · smaller than the amount to place — book it and keep the rest open, or Add to split`
                            : ''}
                        </Text>
                      </BlockStack>
                      <ButtonGroup>
                        {c.deduct ? null : splitItems.length === 0 && c.open < txPlaceAmount - 0.01 ? (
                          <Button
                            size="slim"
                            loading={matchLoading}
                            onClick={() => txAction(reviewTx.BankTransactionID, 'match', { allocations: [{ type: c.type, invoiceId: c.invoiceId, amount: c.open }], keepRemainder: true })}
                          >
                            Match {formatToCurrency(c.open)}, keep {formatToCurrency(Math.round((txPlaceAmount - c.open) * 100) / 100)} open
                          </Button>
                        ) : splitItems.length === 0 ? (
                          <Button
                            size="slim"
                            variant={c.amountExact || c.reasons.length > 0 ? 'primary' : 'secondary'}
                            loading={matchLoading}
                            onClick={() => txAction(reviewTx.BankTransactionID, 'match', { type: c.type, invoiceId: c.invoiceId })}
                          >
                            {!c.amountExact && c.open > txPlaceAmount + 0.01 ? 'Match as partial' : 'Match'}
                          </Button>
                        ) : null}
                        <Button size="slim" variant={splitItems.length === 0 && !c.deduct ? 'secondary' : 'primary'} disabled={inSplit(c)} onClick={() => addToSplit(c)}>
                          {inSplit(c) ? 'Added' : c.deduct ? 'Deduct' : 'Add'}
                        </Button>
                      </ButtonGroup>
                    </InlineStack>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Modal.Section>
        </>
      )}
    </Modal>
  );

  //---------------------------------------------------------------------------
  // page
  //---------------------------------------------------------------------------
  const tabs = [
    { id: 'transactions', content: 'Transactions' },
    { id: 'payments', content: 'Payments' },
    { id: 'accounts', content: 'Accounts' }
  ];

  return (
    <Page
      fullWidth
      title="Banking"
      subtitle={status?.linked ? `Ponto connected (${status.environment}) · last sync: ${status.lastSync ? formatDateTimeShort(status.lastSync) : 'never'}` : undefined}
      primaryAction={{ content: 'Sync now', onAction: handleSync, loading: syncing, disabled: !status?.linked }}
      secondaryActions={status?.configured && !status?.linked && status?.integrationType === 'connect' ? [{ content: 'Connect Ponto', onAction: handleConnect }] : []}
    >
      <TabTitle title="Banking" />
      <BlockStack gap="300">
        {statusBanner}
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
        {selectedTab === 0 && transactionsTab}
        {selectedTab === 1 && paymentsTab}
        {selectedTab === 2 && accountsTab}
      </BlockStack>
      {matchModal}
      {toast && <Toast content={toastContent} onDismiss={() => setToast(false)} />}
      {toastError && <Toast content={toastContent} error onDismiss={() => setToastError(false)} />}
    </Page>
  );
}
